import { sumBalances } from "../collectionUtils";

const getNodePrimaryValue = (node) => {
  if (Array.isArray(node?.summary?.sub_totals) && node.summary.sub_totals.length > 0) {
    return Number(node.summary.sub_totals[0]) || 0;
  }

  if (Array.isArray(node?.values) && node.values.length > 0) {
    return Number(node.values[0]) || 0;
  }

  return Number(node?.balance || node?.amount || 0);
};

const normalizeLineItems = (items = [], total = 0) =>
  items
    .map((item) => {
      const value = getNodePrimaryValue(item);
      return {
        name: item?.label || item?.name || item?.account_name || item?.group || "Unknown",
        value,
        percentage: total ? Number(((value / total) * 100).toFixed(2)) : 0,
      };
    })
    .filter((item) => item.value !== 0 || item.name);

const flattenNodes = (nodes = [], parentPath = []) =>
  nodes.flatMap((node) => {
    const currentPath = [...parentPath, node?.label || node?.group || node?.id || "Unknown"];
    const currentNode = {
      name: node?.label || node?.group || node?.id || "Unknown",
      value: getNodePrimaryValue(node),
      path: currentPath.join(" > "),
      depth: parentPath.length,
      hasChildren: Array.isArray(node?.children) && node.children.length > 0,
    };

    return [
      currentNode,
      ...flattenNodes(Array.isArray(node?.children) ? node.children : [], currentPath),
    ];
  });

const findBalanceSheetRow = (rows = [], groupName) =>
  rows.find(
    (row) =>
      row?.group === groupName ||
      row?.id === groupName ||
      row?.label?.toUpperCase?.() === groupName
  );

const formatBalanceSheetTreeData = (data) => {
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const assetsRow = findBalanceSheetRow(rows, "ASSET");
  const liabilitiesRow = findBalanceSheetRow(rows, "LIABILITY");
  const equityRow = findBalanceSheetRow(rows, "EQUITY");

  const totalAssets = getNodePrimaryValue(assetsRow);
  const totalLiabilities = getNodePrimaryValue(liabilitiesRow);
  const totalEquity = getNodePrimaryValue(equityRow);

  return {
    totals: { totalAssets, totalLiabilities, totalEquity },
    assets: normalizeLineItems(assetsRow?.children || [], totalAssets),
    liabilities: normalizeLineItems(liabilitiesRow?.children || [], totalLiabilities),
    equity: normalizeLineItems(equityRow?.children || [], totalEquity),
    ratios: {
      debtRatio: totalAssets ? totalLiabilities / totalAssets : 0,
      equityRatio: totalAssets ? totalEquity / totalAssets : 0,
      debtToEquity: totalEquity ? totalLiabilities / totalEquity : 0,
    },
    topLineItems: flattenNodes(rows)
      .filter((item) => !item.hasChildren && item.value !== 0)
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
      .slice(0, 20),
  };
};

const mapItems = (items = [], total = 0) =>
  items.map((item) => ({
    name: item.name || item.account_name,
    value: item.balance || item.amount || 0,
    percentage: total ? Number((((item.balance || item.amount || 0) / total) * 100).toFixed(2)) : 0,
  }));

export const formatBalanceSheetData = (data) => {
  if (!data) {
    return null;
  }

  if (Array.isArray(data?.rows)) {
    return formatBalanceSheetTreeData(data);
  }

  const totalAssets = sumBalances(data.assets);
  const totalLiabilities = sumBalances(data.liabilities);
  const totalEquity = sumBalances(data.equity);

  return {
    totals: { totalAssets, totalLiabilities, totalEquity },
    assets: mapItems(data.assets, totalAssets),
    liabilities: mapItems(data.liabilities, totalLiabilities),
    equity: mapItems(data.equity, totalEquity),
    ratios: {
      debtRatio: totalAssets ? totalLiabilities / totalAssets : 0,
      equityRatio: totalAssets ? totalEquity / totalAssets : 0,
      debtToEquity: totalEquity ? totalLiabilities / totalEquity : 0,
    },
  };
};

export const hasMeaningfulFormattedAnalysis = (reportData) => {
  if (!reportData || typeof reportData !== "object") {
    return false;
  }

  const totals = reportData.totals
    ? Object.values(reportData.totals).some((value) => Number(value) !== 0)
    : false;
  const arrays = ["assets", "liabilities", "equity", "items", "details"];

  return totals || arrays.some((key) => Array.isArray(reportData[key]) && reportData[key].length > 0);
};

export const buildCompactFinancialData = (financialData, formattedAnalysis, reportType) => {
  const flattenedRows = flattenNodes(financialData?.rows || []);

  return {
    reportType,
    overview: {
      label: financialData?.overview?.label || null,
      date: financialData?.overview?.date || null,
      currency: financialData?.overview?.currency || null,
      groupBy: financialData?.overview?.group_by || null,
    },
    columns: (financialData?.columns || []).map((column) => ({
      id: column?.id,
      label: column?.label,
      date: column?.metadata?.date || null,
    })),
    topLevelRows: (financialData?.rows || []).map((row) => ({
      label: row?.label || row?.group || row?.id,
      value: getNodePrimaryValue(row),
    })),
    normalizedAnalysis: formattedAnalysis,
    keyLineItems: flattenedRows
      .filter((row) => !row.hasChildren && row.value !== 0)
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
      .slice(0, 15),
  };
};
